"use client";

import { useEffect, useRef, useCallback } from "react";
import Lenis from "lenis";

export function useLenis() {
  const lenisRef = useRef<Lenis | null>(null);

  const handleAnchorClick = useCallback((e: Event) => {
    e.preventDefault();
    const anchor = e.currentTarget as HTMLAnchorElement;
    const target = document.querySelector(anchor.getAttribute("href")!);
    if (target && lenisRef.current) {
      lenisRef.current.scrollTo(target as HTMLElement);
    }
  }, []);

  useEffect(() => {
    // Defer Lenis initialization until idle to reduce TBT
    const initLenis = () => {
      const lenis = new Lenis({
        duration: 1.0,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        gestureOrientation: "vertical",
        smoothWheel: true,
        touchMultiplier: 1.5,
        syncTouch: false,
      });
      
      lenisRef.current = lenis;

      function raf(time: number) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      // Handle anchor links
      const anchors = document.querySelectorAll('a[href^="#"]');
      anchors.forEach((anchor) => {
        anchor.addEventListener("click", handleAnchorClick);
      });

      return () => {
        anchors.forEach((anchor) => {
          anchor.removeEventListener("click", handleAnchorClick);
        });
        lenis.destroy();
        lenisRef.current = null;
      };
    };

    // Use requestIdleCallback to defer initialization
    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(initLenis, { timeout: 2000 });
      return () => {
        cancelIdleCallback(idleId);
        if (lenisRef.current) {
          lenisRef.current.destroy();
          lenisRef.current = null;
        }
      };
    } else {
      // Fallback for Safari
      const timeoutId = setTimeout(initLenis, 100);
      return () => {
        clearTimeout(timeoutId);
        if (lenisRef.current) {
          lenisRef.current.destroy();
          lenisRef.current = null;
        }
      };
    }
  }, [handleAnchorClick]);
}
